
import React, { useEffect } from 'react';
import { GenericScheduleProps, Schedule13UaeDividendsData, UaeDividendItem } from '../../types';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { safeParseFloat } from '../../utils/calculations';

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h4 className="text-md font-semibold text-sky-700 mt-4 mb-2 border-b pb-1">{title}</h4>
);

export const Schedule13_UaeDividends: React.FC<GenericScheduleProps<Schedule13UaeDividendsData>> = ({ scheduleData, onChange, errors, t, fullFormData }) => {
  
  const handleItemChange = (index: number, field: keyof UaeDividendItem, value: string) => {
    const updatedItems = scheduleData.items.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    onChange('items', updatedItems);
  };

  const addItem = () => {
    onChange('items', [
      ...scheduleData.items,
      { 
        id: Date.now().toString(), 
        companyName: '', 
        trn: '', 
        amount: '' 
      }
    ]);
  };

  const removeItem = (id: string) => {
    onChange('items', scheduleData.items.filter(item => item.id !== id));
  };

  useEffect(() => {
    const total = scheduleData.items.reduce((sum, item) => sum + safeParseFloat(item.amount), 0);
    if (scheduleData.totalDividendsReceived !== total.toFixed(2)) {
      onChange('totalDividendsReceived', total.toFixed(2));
    }
  }, [scheduleData.items, scheduleData.totalDividendsReceived, onChange]);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-sky-600">{t('sch13_title')}</h3>
      
      {scheduleData.items.map((item, index) => (
        <div key={item.id} className="p-3 border rounded-md space-y-3 bg-slate-50 relative">
            <p className="text-sm font-semibold text-gray-600">{t('sch13_company_name')} {index + 1}</p>
            <Input 
                label={t('sch13_company_name')} 
                type="text" 
                id={`sch13CompanyName-${item.id}`} 
                value={item.companyName} 
                onChange={(e) => handleItemChange(index, 'companyName', e.target.value)} 
                error={(errors?.items as any)?.[index]?.companyName as string}
            />
            <Input 
                label={t('sch13_company_trn')} 
                type="text" 
                id={`sch13CompanyTrn-${item.id}`} 
                value={item.trn} 
                onChange={(e) => handleItemChange(index, 'trn', e.target.value)} 
                error={(errors?.items as any)?.[index]?.trn as string}
            />
            <Input 
                label={t('sch13_profit_dist_received')} 
                type="number" 
                id={`sch13Amount-${item.id}`} 
                value={item.amount} 
                onChange={(e) => handleItemChange(index, 'amount', e.target.value)} 
                error={(errors?.items as any)?.[index]?.amount as string}
            />
            <Button 
                onClick={() => removeItem(item.id)} 
                variant="danger_outline" 
                size="sm" 
                className="absolute top-2 right-2"
                aria-label={t('remove_item_button') + ' for ' + item.companyName}
            >
                {t('remove_item_button')}
            </Button>
        </div>
      ))}
      <Button onClick={addItem} variant="secondary" size="sm">{t('sch13_add_dividend_button')}</Button>

      <Input 
        label={t('sch13_total_dividends_received')} 
        type="text" 
        id="sch13TotalDividends" 
        value={scheduleData.totalDividendsReceived || '0.00'} 
        readOnly 
        className="bg-gray-100 font-semibold" 
      />
    </div>
  );
};
